import React, { useState } from 'react';
import { useTranslation } from '../context/TranslationContext';

/**
 * Thin top strip shown above the main header.
 * Left: "Official website: Best Rate Guaranteed" (trust / book-direct).
 * Right: hotel city (opens a location modal) + phone (tel:) + email (mailto:) — REAL data only.
 * No fake data: each element renders only if its value exists. If nothing is available, the bar hides.
 */
const TopContactBar = ({ accommodationDetail }) => {
  const { t } = useTranslation();
  const [showLocation, setShowLocation] = useState(false);

  const d = accommodationDetail || {};
  const firstOf = (v) => (typeof v === 'string' ? (v.split(',')[0] || '').trim() : '');

  const phone = firstOf(d.contactNo);
  const email = firstOf(d.emailId);
  const cityName = (typeof d.CityName === 'string' ? d.CityName : (typeof d.cityName === 'string' ? d.cityName : '')).trim();
  const hotelName = d.accommodationName || d.AccommodationName || '';
  const address = (typeof d.Address === 'string' ? d.Address : (typeof d.address === 'string' ? d.address : '')).trim();

  const loc = d.location || d.Location || {};
  const lat = loc.Latitude || loc.latitude || '';
  const lng = loc.Longitude || loc.longitude || '';
  const hasMap = !!(lat && lng);
  const hasLocationData = hasMap || !!address;

  // Website: normalise to an absolute URL
  let siteUrl = (typeof d.website === 'string' ? d.website : (typeof d.Website === 'string' ? d.Website : '')).trim();
  if (siteUrl && !/^https?:\/\//i.test(siteUrl)) siteUrl = `https://${siteUrl}`;

  // Nothing to show → don't render the bar at all
  if (!phone && !email && !cityName) return null;

  const mapQuery = hasMap ? `${lat},${lng}` : address;
  const mapEmbedSrc = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=15&output=embed`;
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;

  const PinIcon = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );

  return (
    <>
      <div
        className="w-full border-b text-white"
        style={{
          backgroundColor: 'var(--primary-color, #1a1a1a)',
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.18), rgba(0,0,0,0.18))',
          borderColor: 'rgba(255,255,255,0.12)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 py-1.5 text-xs sm:text-sm">
            {/* Left: Official website (clickable) / Best Rate Guaranteed */}
            <div className="min-w-0 truncate font-semibold text-white">
              {siteUrl ? (
                <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 underline-offset-2 hover:decoration-white">
                  {t('header.officialWebsite')}
                </a>
              ) : (
                <span>{t('header.officialWebsite')}</span>
              )}
              <span>: {t('header.bestRateGuaranteed')}</span>
            </div>

            {/* Right: city | phone | email */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {cityName && (
                hasLocationData ? (
                  <button
                    type="button"
                    onClick={() => setShowLocation(true)}
                    className="hidden sm:inline-flex items-center gap-1.5 text-white whitespace-nowrap underline decoration-white/70 underline-offset-2 hover:decoration-white"
                  >
                    <PinIcon className="h-4 w-4 flex-shrink-0" />
                    {cityName}
                  </button>
                ) : (
                  <span className="hidden sm:inline-flex items-center gap-1.5 text-white whitespace-nowrap">
                    <PinIcon className="h-4 w-4 flex-shrink-0" />
                    {cityName}
                  </span>
                )
              )}
              {/* Mobile: pin icon opens location (icon only) */}
              {hasLocationData && (
                <button
                  type="button"
                  onClick={() => setShowLocation(true)}
                  className="sm:hidden inline-flex items-center text-white underline decoration-white/70 underline-offset-2"
                  aria-label={cityName || 'Location'}
                >
                  <PinIcon className="h-4 w-4" />
                </button>
              )}
              {cityName && (phone || email) && <span className="hidden sm:inline text-white/40">|</span>}
              {phone && (
                <a href={`tel:${phone.replace(/\s+/g, '')}`} className="inline-flex items-center gap-1.5 text-white hover:text-white/80 whitespace-nowrap">
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="hidden sm:inline">{phone}</span>
                </a>
              )}
              {phone && email && <span className="hidden sm:inline text-white/40">|</span>}
              {email && (
                <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 text-white hover:text-white/80 min-w-0">
                  <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="hidden sm:inline truncate max-w-[140px] sm:max-w-none">{email}</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Location modal */}
      {showLocation && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999]"
          onClick={() => setShowLocation(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[88vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grab handle — mobile bottom-sheet only */}
            <div className="sm:hidden pt-2.5 pb-1 flex justify-center flex-shrink-0">
              <span className="h-1.5 w-10 rounded-full bg-gray-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900 leading-tight">{hotelName || cityName}</h3>
                {siteUrl && (
                  <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="mt-1.5 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-full hover:bg-emerald-100">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('header.officialWebsite')}
                  </a>
                )}
              </div>
              <button onClick={() => setShowLocation(false)} className="flex-shrink-0 text-gray-400 hover:text-gray-700" aria-label="Close">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-y-auto p-5 gap-5">
              {/* Map */}
              <div className="md:w-[55%] flex-shrink-0">
                <a
                  href={mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in Google Maps"
                  className="block rounded-xl overflow-hidden shadow-sm border border-gray-200"
                >
                  <iframe
                    title="Hotel location map"
                    src={mapEmbedSrc}
                    className="w-full h-64 md:h-[420px] border-0 pointer-events-none"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </a>
              </div>

              {/* Details */}
              <div className="md:w-[45%] space-y-5 md:pt-1">
                {address && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1.5">{t('header.whereWeAre')}</h4>
                    <p className="flex items-start gap-2 text-sm text-gray-700">
                      <PinIcon className="h-5 w-5 flex-shrink-0 text-emerald-600 mt-0.5" />
                      <span>{address}{cityName ? `, ${cityName}` : ''}</span>
                    </p>
                  </div>
                )}

                {hasMap && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1.5">{t('header.gpsCoordinates')}</h4>
                    <p className="text-sm text-gray-700">Latitude: {lat}</p>
                    <p className="text-sm text-gray-700">Longitude: {lng}</p>
                  </div>
                )}

                {(email || phone) && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1.5">{t('header.contactUs')}</h4>
                    {email && (
                      <a href={`mailto:${email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-700 mb-1">
                        <svg className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {email}
                      </a>
                    )}
                    {phone && (
                      <a href={`tel:${phone.replace(/\s+/g, '')}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-700">
                        <svg className="h-4 w-4 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {phone}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-3 border-t border-gray-100">
              <button onClick={() => setShowLocation(false)} className="px-5 py-2 rounded-full border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50">
                {t('header.close') || 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopContactBar;
